import React, { useEffect, useState } from 'react';
import { obtenerConfiguracion } from '../../services/configTiendaApi';
import { getCategorias } from '../../services/categoriasServicio';
import { getProductosCatalogo } from '../../services/productosApi';
import config from '../../config/apiConfig';

const CheckIcon = ({ hecho }) => (
    <div style={{
        width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: hecho ? '#22c55e' : '#e5e7eb',
        color: hecho ? '#fff' : '#9ca3af',
        fontWeight: 800, fontSize: '0.85rem'
    }}>
        {hecho ? '✓' : ''}
    </div>
);

function InicioRestaurante() {
    const [loading, setLoading] = useState(true);
    const [tienda, setTienda] = useState({ nombre: '', urlLogo: '' });
    const [numCategorias, setNumCategorias] = useState(0);
    const [numProductos, setNumProductos] = useState(0);
    const [numStaff, setNumStaff] = useState(0);
    const [numMesas, setNumMesas] = useState(0);

    useEffect(() => {
        const cargar = async () => {
            try {
                const token = localStorage.getItem('token');
                const headers = { token };
                const [cfg, categorias, catalogo, staffRes, mesasRes] = await Promise.all([
                    obtenerConfiguracion().catch(() => null),
                    getCategorias().catch(() => []),
                    getProductosCatalogo().catch(() => ({ productos: [] })),
                    fetch(`${config.api_url}/api/staff`, { headers }).then(r => r.json()).catch(() => ({ data: [] })),
                    fetch(`${config.api_url}/api/mesas`, { headers }).then(r => r.json()).catch(() => ({ data: [] })),
                ]);
                if (cfg) {
                    setTienda({
                        nombre: cfg.nombre || '',
                        urlLogo: cfg.url_logo || cfg.urlLogo || '',
                    });
                }
                setNumCategorias((categorias || []).length);
                setNumProductos(((catalogo && catalogo.productos) || []).length);
                setNumStaff(((staffRes && staffRes.data) || []).length);
                setNumMesas(((mesasRes && mesasRes.data) || []).length);
            } finally {
                setLoading(false);
            }
        };
        cargar();
    }, []);

    const pasos = [
        { id: 'logo', label: 'Sube el logo de tu negocio', hecho: !!tienda.urlLogo, href: '/configuracion/tienda/' },
        { id: 'categorias', label: 'Crea tus categorías de productos', hecho: numCategorias > 0, href: '/categorias/' },
        { id: 'productos', label: 'Agrega tu carta con precios', hecho: numProductos > 0, href: '/productos/home/' },
        { id: 'mesas', label: 'Configura las mesas de tu local', hecho: numMesas > 0, href: '/admin/mesas/', desc: 'Nombre, sector y capacidad de cada mesa' },
        { id: 'staff', label: 'Crea el PIN de acceso de tu equipo', hecho: numStaff > 0, href: '/staff/', desc: 'Garzones, cajeros y cocina con sus propios PINs' },
    ];

    const completados = pasos.filter(p => p.hecho).length;
    const listo = completados === pasos.length;

    if (loading) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center">
                <div className="spinner-border text-primary"></div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 bg-light py-4">
            <div className="container" style={{ maxWidth: '720px' }}>
                <div className="text-center mb-4">
                    <h1 className="h3 font-weight-bold text-dark mb-1">
                        {listo ? '¡Tu restaurante está listo! 🎉' : `Hola${tienda.nombre ? ', ' + tienda.nombre : ''} 👋`}
                    </h1>
                    <p className="text-muted mb-0">
                        {listo
                            ? 'Ya puedes recibir pedidos y gestionar tu salón completo.'
                            : 'Sigue estos pasos para poner en marcha tu restaurante.'}
                    </p>
                </div>

                <div className="card shadow-sm border-0 rounded-lg p-4 mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="font-weight-bold text-dark">Progreso de configuración</span>
                        <span className="badge badge-pill" style={{
                            background: listo ? '#dcfce7' : '#fff7ed',
                            color: listo ? '#16a34a' : '#c2410c',
                            fontWeight: 700, padding: '6px 12px'
                        }}>{completados}/{pasos.length}</span>
                    </div>
                    <div className="progress mb-4" style={{ height: '8px', borderRadius: '8px' }}>
                        <div className="progress-bar" style={{ width: `${(completados / pasos.length) * 100}%`, borderRadius: '8px', background: listo ? '#22c55e' : '#f97316' }}></div>
                    </div>

                    {pasos.map((paso) => (
                        <a key={paso.id} href={paso.href} className="text-decoration-none">
                            <div className="d-flex align-items-start py-3" style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <CheckIcon hecho={paso.hecho} />
                                <div className="ml-3" style={{ flex: 1 }}>
                                    <div style={{ color: paso.hecho ? '#9ca3af' : '#1a1a2e', textDecoration: paso.hecho ? 'line-through' : 'none', fontWeight: 500 }}>
                                        {paso.label}
                                    </div>
                                    {paso.desc && !paso.hecho && (
                                        <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '2px' }}>{paso.desc}</div>
                                    )}
                                </div>
                                {!paso.hecho && (
                                    <span className="ml-auto text-primary" style={{ fontSize: '0.85rem', fontWeight: 600, flexShrink: 0 }}>Ir →</span>
                                )}
                            </div>
                        </a>
                    ))}
                </div>

                {listo && (
                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="card shadow-sm border-0 rounded-lg p-3 text-center h-100" style={{ background: 'linear-gradient(135deg, #1a1a2e, #302b63)', color: '#fff' }}>
                                <div style={{ fontSize: '1.8rem', marginBottom: '6px' }}>🍽️</div>
                                <h6 className="font-weight-bold mb-2">Gestión de mesas</h6>
                                <a href="/mesas/" className="btn btn-light btn-sm font-weight-bold">Ver salón →</a>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="card shadow-sm border-0 rounded-lg p-3 text-center h-100" style={{ background: 'linear-gradient(135deg, #1a1a2e, #302b63)', color: '#fff' }}>
                                <div style={{ fontSize: '1.8rem', marginBottom: '6px' }}>🖥️</div>
                                <h6 className="font-weight-bold mb-2">Punto de venta</h6>
                                <a href="/ventas/tpv" className="btn btn-light btn-sm font-weight-bold">Ir al TPV →</a>
                            </div>
                        </div>
                    </div>
                )}

                {!listo && (
                    <div className="card border-0 shadow-sm rounded-lg p-3" style={{ background: '#f8f9fa' }}>
                        <p className="mb-0 small text-muted text-center">
                            ¿Necesitas ayuda? Escríbenos por <a href="https://wa.me/56920039433" target="_blank" rel="noreferrer" className="font-weight-bold">WhatsApp</a>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default InicioRestaurante;
